<template>
  <b-modal
    :visible="isModalActive"
    :title="$t('HTML')"
    ok-title="Accept"
    size="lg"
    @change="(val) => $emit('update:is-modal-active', val)"
  >
    <b-overlay :show="dataLoading" rounded="sm">
      <!-- Editor Mode Toggle -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">{{ $t("Editor Mode") }}</h6>
        <div class="d-flex align-items-center">
          <span class="mr-2">{{ $t("Code") }}</span>
          <b-form-checkbox v-model="isVisualEditor" switch size="lg" />
          <span class="ml-2">{{ $t("Visual") }}</span>
        </div>
      </div>

      <b-tabs v-model="tabIndex">
        <b-tab
          v-for="(itemLang, indexLang) in language"
          :key="indexLang"
          :title="$t(itemLang.name)"
        >
          <b-form class="mt-2">
            <!-- Code Editor Mode -->
            <div v-if="!isVisualEditor">
              <label :for="'code-' + itemLang.name" class="form-label">{{
                $t("HTML Code")
              }}</label>
              <b-form-textarea
                v-if="html[indexLang]"
                :id="'code-' + itemLang.name"
                :value="html[indexLang]['value']"
                :name="itemLang.name"
                :placeholder="$t('Enter HTML code...')"
                rows="10"
                class="code-editor"
                @input="inputLanguage($event, itemLang.lang, indexLang, 'html')"
              />
            </div>

            <!-- Visual Editor Mode -->
            <div v-else>
              <label :for="'visual-' + itemLang.name" class="form-label">{{
                $t("Visual Editor")
              }}</label>
              <vue-editor
                v-if="html[indexLang]"
                :id="'visual-' + itemLang.name"
                :value="html[indexLang]['value']"
                :editor-toolbar="customToolbar"
                :editor-options="editorOptions"
                @input="inputLanguage($event, itemLang.lang, indexLang, 'html')"
              />
            </div>

            <!-- Preview -->
            <div
              v-if="html[indexLang] && html[indexLang]['value']"
              class="mt-3"
            >
              <h6>{{ $t("Preview") }}</h6>
              <div
                class="preview-container p-3 border rounded"
                v-html="html[indexLang]['value']"
              />
            </div>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-overlay>

    <template #modal-footer>
      <div class="w-100">
        <p class="float-left mb-0" />
        <b-button
          variant="success"
          size="sm"
          class="mr-1"
          @click.stop="validationForm()"
        >
          <feather-icon icon="SaveIcon" />
        </b-button>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          class=""
          @click="$emit('update:is-modal-active', false)"
        >
          {{ $t("Close") }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BTabs,
  BTab,
  BCardText,
  BFormCheckbox,
  BButton,
  BOverlay,
  BForm,
  BFormTextarea,
} from "bootstrap-vue";
import { required } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";

import { VueEditor } from "vue2-editor";

import store from "@/store";

export default {
  components: {
    // eslint-disable-next-line vue/no-unused-components
    vSelect,
    BModal,
    // eslint-disable-next-line vue/no-unused-components
    BRow,
    // eslint-disable-next-line vue/no-unused-components
    BCol,
    BTabs,
    BTab,
    // eslint-disable-next-line vue/no-unused-components
    BCardText,
    BFormCheckbox,
    // eslint-disable-next-line vue/no-unused-components
    BButton,
    BOverlay,
    BForm,
    BFormTextarea,
    VueEditor,
  },
  directives: {
    Ripple,
  },
  mixins: [togglePasswordVisibility],
  model: {
    prop: "isModalActive",
    event: "update:is-modal-active",
  },
  props: {
    storeModuleName: {
      type: String,
      required: true,
    },
    isModalActive: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
      default: () => {},
    },
    show: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      dataId: null,
      dataLoading: false,
      tabIndex: 0,
      html: [],
      required,
      isVisualEditor: false,
      customToolbar: [
        ["bold", "italic", "underline"],
        ["blockquote", "code-block"],
        [{ header: 1 }, { header: 2 }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ["clean"],
        ["link", "image", "video"],
      ],
      editorOptions: {
        theme: "snow",
        placeholder: "Enter your content...",
        modules: {
          toolbar: {
            container: [
              ["bold", "italic", "underline"],
              ["blockquote", "code-block"],
              [{ header: 1 }, { header: 2 }],
              [{ list: "ordered" }, { list: "bullet" }],
              [{ script: "sub" }, { script: "super" }],
              [{ indent: "-1" }, { indent: "+1" }],
              [{ direction: "rtl" }],
              [{ size: ["small", false, "large", "huge"] }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ color: [] }, { background: [] }],
              [{ font: [] }],
              [{ align: [] }],
              ["clean"],
              ["link", "image", "video"],
            ],
          },
        },
      },
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    language() {
      return store.state.appConfig.systemConfig
        ? store.state.appConfig.systemConfig.language
        : [];
    },
  },
  watch: {
    isModalActive(val) {
      if (val) {
        if (Object.entries(this.data).length === 0) {
          this.$refs.formData.reset();
          this.initValues();
        } else {
          const { _id } = this.data;
          this.dataId = _id;
          this.html = this.data.html || [];
          if (this.html.length !== this.language.length) {
            for (let index = 0; index < this.language.length; index += 1) {
              const indexLanguage = this.html.findIndex(
                (element) => element.lang === this.language[index].lang
              );
              if (indexLanguage < 0) {
                this.html.push({
                  lang: this.language[index].lang, // th , en
                  value: "",
                });
              }
            }
            for (let index = 0; index < this.html.length; index += 1) {
              const indexLanguage = this.language.findIndex(
                (element) => element.lang === this.html[index].lang
              );
              if (indexLanguage < 0) {
                this.html.splice(index, 1);
              }
            }
          }
        }
      }
    },
  },
  methods: {
    initValues() {
      this.dataId = null;
      this.dataResponse = null;
    },
    inputLanguage(input, lang, index, type) {
      console.log(input, lang, index, type);

      if (type === "html") {
        if (this.html[index].lang === lang) {
          this.html[index].value = input;
        }
      }
    },
    validationForm() {
      const obj = {
        id: this.dataId,
        html: this.html,
      };
      this.$emit("update:show", true);
      if (this.dataId !== null) {
        store
          .dispatch(`${this.storeModuleName}/html`, obj)
          .then((result) => {
            this.$emit("update:is-sidebar-active", false);
            store
              .dispatch(`${this.storeModuleName}/get`, {
                currentPage: this.currentPage,
                pageSize: 0,
                searchQuery: "",
              })
              .then((result) => {
                this.$emit("update:show", false);
              });
            // this.showToast('success', 'bottom-right')
          })
          .catch((error) => {
            console.log("fetchUsers Error : ", error);
            this.$toast({
              component: ToastificationContent,
              position: "bottom-right",
              props: {
                title: this.$t("Error"),
                icon: "ErrorIcon",
                variant: "danger",
                text: this.$t(error.response.data.message),
              },
            });
            this.$emit("update:show", false);
            // this.showToast('danger', 'bottom-right')
          });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
